/**
 * Car BODY against the arena — the contact the suspension cannot produce.
 *
 * ./suspension casts four rays along the car's local -up. That is exactly right
 * for driving (it is how a car climbs a floor fillet onto a wall) and it does
 * nothing at all for a car flying sideways into a wall, landing hard enough to
 * bottom out, or scraping a corner: none of those are below a wheel. RocketSim
 * gets that case from Bullet's contact manifold between the car's box and the
 * arena mesh. This file is that manifold, and the impulse response to it.
 *
 * ===========================================================================
 * WHAT IT MUST NOT DO
 * ===========================================================================
 *
 * It must not fire while the car is driving. The springs hold the car's origin
 * at 17 uu and the hitbox sits 20.755 uu further up with a half-height of
 * 19.33, so the bottom of the box clears the surface by 18.4 uu on every
 * surface the car can drive on. The tightest case is a floor fillet, where a
 * 120.5 uu box spans a 256 uu radius arc: the sagitta is 256 - sqrt(256^2 -
 * 60.25^2) = 7.2 uu, still 11 uu of clearance. `test/physics/car-arena.test.ts`
 * drives floor, wall and ceiling and asserts zero contacts on every tick — that
 * assertion is the whole reason this module can be trusted not to change how
 * the car drives.
 *
 * ===========================================================================
 * THE RESPONSE
 * ===========================================================================
 *
 * Per contact, in this order:
 *
 *   1. DEPENETRATE along the contact normal. Position only; there is no
 *      Baumgarte term feeding penetration back into the velocity, because that
 *      would add energy at exactly the shallow-contact depths a scrape produces.
 *
 *   2. NORMAL IMPULSE, with restitution ARENA_COLLISION_RESTITUTION. Applied
 *      only where the contact is actually moving INTO the surface — a car being
 *      carried out of a wall by last tick's resolution must not be hit again.
 *
 *   3. FRICTION IMPULSE, opposing the tangential velocity at the contact point,
 *      Coulomb-clamped to ARENA_COLLISION_FRICTION times the normal impulse.
 *
 * Both impulses are full rigid-body impulses: they change the angular velocity
 * as well as the linear one, through the car's inertia tensor — the BODY's, via
 * `carInertiaPerMass(car.config)`, so a Dominus is levered by a wall hit
 * differently from an Octane. That is the difference between "a car that hits a wall
 * nose-first stops" and "a car that hits a wall nose-first stops AND is levered
 * nose-up by the hit", and it is why the old half-space backstop looked wrong:
 * it could only ever remove a velocity component.
 *
 * Mass divides out of everything here. CAR_MASS never appears: the impulses are
 * computed per unit mass, and the inertia is used as I/m, which is the form
 * ./constants publishes it in.
 *
 * ===========================================================================
 * WHY THE CONTACT IS A PATCH AND NOT A POINT
 * ===========================================================================
 *
 * A single contact point cannot resist rotation about itself, so resolving a
 * FLAT hit at one point is qualitatively wrong: a car meeting a wall square-on
 * would be spun to the angular speed cap by the lever arm between the wall and
 * its centre of mass, because nothing tells the solver that the whole nose is
 * touching, not just its middle.
 *
 * So each contact is solved at the box's SUPPORT SET in the -normal direction —
 * the set of box vertices that are furthest into the surface. It has one member
 * when the box meets the surface on a corner, two on an edge and four when a
 * face is flat against it. Iterating impulses over those points (Gauss-Seidel:
 * each one sees what the previous ones did) converges on the behaviour of a
 * contact PATCH, so a flat nose-on hit stops without spinning while a corner
 * scrape still spins exactly as hard as it should. The set is derived from the
 * normal's components in the box's own frame, so it costs three comparisons.
 *
 * ===========================================================================
 * SHARED WITH ./car-car
 * ===========================================================================
 *
 * `angularTerm`, `applyImpulse`, `supportPoints`, `tangentBasis` and
 * `pointKinematicsInto` are exported, and are the SAME solver primitives
 * ./car-car uses for car-vs-car contact. Everything here is expressed per unit
 * mass, so the only thing that differs between the one-body and the two-body
 * case is the effective inverse mass: `1 + angularTerm` against the immovable
 * arena, `2 + angularTerm(a) + angularTerm(b)` between two cars of equal
 * CAR_MASS. The rest — accumulate-and-clamp, the fixed friction basis, the
 * support-set patch — is identical, and is shared rather than copied.
 */

import { carInertiaPerMass, type InertiaPerMass } from './car-config';
import type { CarState } from './car-state';
import {
  ARENA_COLLISION_FRICTION,
  ARENA_COLLISION_RESTITUTION,
  CAR_MAX_ANGULAR_SPEED,
  CAR_MAX_SPEED,
} from './constants';
import { clampLengthBy, vec3, type Mat3, type Vec3 } from './math';
import { carHitboxInto, makeCarHitbox, type OBB } from './obb';
import { makeContact, type ArenaGeometry, type Contact } from './surface';

/**
 * Contacts resolved per pass. The car's box can genuinely touch three surfaces
 * at once (a floor-wall-wall corner) and a fourth slot leaves room for the
 * fillet between two of them.
 */
const MAX_CONTACTS = 4;

/**
 * Resolution passes. Each one re-queries, because resolving against the floor
 * moves the box into the wall and the wall contact was measured at the old
 * pose — the same argument as the ball's BALL_CONTACT_PASSES, with a smaller
 * count because the box is resolved along face normals only and so converges
 * faster. A pass that finds nothing exits immediately, so the cost of this in
 * the common (no contact) case is one BVH query.
 */
const CONTACT_PASSES = 2;

/**
 * Gauss-Seidel sweeps over the support points within one pass. Eight is where
 * the residual on a square 2000 uu/s wall hit stops moving: the car is levered
 * nose-up by the offset between its box and its centre of mass, and everything
 * else is under 0.01 rad/s.
 */
const SOLVER_ITERATIONS = 8;

/** Below this penetration a contact is treated as already resolved. */
const DEPTH_EPSILON = 1e-6;

/**
 * A normal component below this counts as zero when deriving the support set,
 * i.e. the box is treated as flat on that axis. It is a geometric tolerance on
 * a unit vector, not a physical quantity.
 */
const FLAT_EPSILON = 1e-9;

/** Maximum support points per contact: a face. */
const PATCH_MAX = 4;

/** Per-car working state. One of these is held by the world, not allocated
 *  per tick — see the perf note in src/sim/world.ts. */
export interface CarArenaScratch {
  box: OBB;
  contacts: Contact[];
  /** Per contact, up to four support points as flat (x, y, z) triples. */
  patch: Float64Array[];
  /** How many of each `patch` entry's points are live. */
  patchSize: Int32Array;
  /** Accumulated normal impulse per point, flattened over contacts. */
  accN: Float64Array;
  /** Accumulated friction impulse per point, in the (t1, t2) basis. */
  accT1: Float64Array;
  accT2: Float64Array;
  /** Target normal separation speed per point: -restitution * approach speed. */
  bias: Float64Array;
  /**
   * Effective inverse masses along the normal and the two friction axes, per
   * point. They depend only on the lever arm and the orientation, neither of
   * which an impulse changes, so they are computed once per pass instead of
   * three times per point per sweep — 24 `angularTerm` calls saved per point.
   */
  kn: Float64Array;
  k1: Float64Array;
  k2: Float64Array;
  /** Friction basis per contact, flattened as (t1, t2) triples. */
  tangents: Float64Array;
}

export const makeCarArenaScratch = (): CarArenaScratch => ({
  box: makeCarHitbox(),
  contacts: Array.from({ length: MAX_CONTACTS }, makeContact),
  patch: Array.from({ length: MAX_CONTACTS }, () => new Float64Array(PATCH_MAX * 3)),
  patchSize: new Int32Array(MAX_CONTACTS),
  accN: new Float64Array(MAX_CONTACTS * PATCH_MAX),
  accT1: new Float64Array(MAX_CONTACTS * PATCH_MAX),
  accT2: new Float64Array(MAX_CONTACTS * PATCH_MAX),
  bias: new Float64Array(MAX_CONTACTS * PATCH_MAX),
  kn: new Float64Array(MAX_CONTACTS * PATCH_MAX),
  k1: new Float64Array(MAX_CONTACTS * PATCH_MAX),
  k2: new Float64Array(MAX_CONTACTS * PATCH_MAX),
  tangents: new Float64Array(MAX_CONTACTS * 6),
});

const _r = vec3(0, 0, 0);
const _pointVel = vec3(0, 0, 0);

/**
 * Angular response denominator for a unit impulse along `n` applied at lever
 * arm `r`: `n . ((I^-1 (r x n)) x r)`, with the inertia taken in the car's
 * local frame where it is diagonal. Returns the value per unit mass, so the
 * caller's effective inverse mass is `1 + this`.
 *
 * EXPORTED for ./car-car, which solves the same contact between two cars rather
 * than between a car and the static arena: there the effective inverse mass is
 * `2 + this(a) + this(b)`, because both bodies move and both have the same
 * CAR_MASS. Everything in this file's solver is per unit mass and so is shared
 * verbatim rather than reimplemented — a second impulse solver with its own
 * conventions is exactly how two contact models drift apart.
 */
export const angularTerm = (
  m: Mat3,
  inertia: InertiaPerMass,
  r: Vec3,
  nx: number,
  ny: number,
  nz: number
): number => {
  // t = r x n, world frame
  const tx = r.y * nz - r.z * ny;
  const ty = r.z * nx - r.x * nz;
  const tz = r.x * ny - r.y * nx;

  // Into the car's frame, divide by the principal moments, back out again.
  const lx = (m[0] * tx + m[3] * ty + m[6] * tz) / inertia.roll;
  const ly = (m[1] * tx + m[4] * ty + m[7] * tz) / inertia.pitch;
  const lz = (m[2] * tx + m[5] * ty + m[8] * tz) / inertia.yaw;
  const wx = m[0] * lx + m[1] * ly + m[2] * lz;
  const wy = m[3] * lx + m[4] * ly + m[5] * lz;
  const wz = m[6] * lx + m[7] * ly + m[8] * lz;

  // (I^-1 (r x n)) x r, dotted with n
  return nx * (wy * r.z - wz * r.y) + ny * (wz * r.x - wx * r.z) + nz * (wx * r.y - wy * r.x);
};

/** Apply an impulse (per unit mass) along (jx, jy, jz) at lever arm `r`.
 *  Shared with ./car-car — see `angularTerm`. */
export const applyImpulse = (
  car: CarState,
  inertia: InertiaPerMass,
  r: Vec3,
  jx: number,
  jy: number,
  jz: number
): void => {
  car.velocity.x += jx;
  car.velocity.y += jy;
  car.velocity.z += jz;

  const m = car.orientation;
  const tx = r.y * jz - r.z * jy;
  const ty = r.z * jx - r.x * jz;
  const tz = r.x * jy - r.y * jx;
  const lx = (m[0] * tx + m[3] * ty + m[6] * tz) / inertia.roll;
  const ly = (m[1] * tx + m[4] * ty + m[7] * tz) / inertia.pitch;
  const lz = (m[2] * tx + m[5] * ty + m[8] * tz) / inertia.yaw;
  car.angularVelocity.x += m[0] * lx + m[1] * ly + m[2] * lz;
  car.angularVelocity.y += m[3] * lx + m[4] * ly + m[5] * lz;
  car.angularVelocity.z += m[6] * lx + m[7] * ly + m[8] * lz;
};

/**
 * The box vertices furthest into the surface along `-normal`, in WORLD space,
 * written into `out` as flat triples. Returns how many were written: 1 for a
 * corner contact, 2 for an edge, 4 for a face.
 */
export const supportPoints = (
  center: Vec3,
  m: Mat3,
  h: Vec3,
  n: Vec3,
  out: Float64Array
): number => {
  // The normal in the box's own frame; there the support set is decided per
  // axis by the sign of the component.
  const nx = m[0] * n.x + m[3] * n.y + m[6] * n.z;
  const ny = m[1] * n.x + m[4] * n.y + m[7] * n.z;
  const nz = m[2] * n.x + m[5] * n.y + m[8] * n.z;

  // Per axis: a definite sign pins the coordinate, a flat axis spans both.
  const spanX = Math.abs(nx) <= FLAT_EPSILON ? 1 : 0;
  const spanY = Math.abs(ny) <= FLAT_EPSILON ? 1 : 0;
  const spanZ = Math.abs(nz) <= FLAT_EPSILON ? 1 : 0;

  let count = 0;
  for (let i = 0; i <= spanX; i++) {
    const sx = spanX ? (i === 0 ? -h.x : h.x) : nx > 0 ? -h.x : h.x;
    for (let j = 0; j <= spanY; j++) {
      const sy = spanY ? (j === 0 ? -h.y : h.y) : ny > 0 ? -h.y : h.y;
      for (let k = 0; k <= spanZ; k++) {
        const sz = spanZ ? (k === 0 ? -h.z : h.z) : nz > 0 ? -h.z : h.z;
        out[count * 3] = center.x + m[0] * sx + m[1] * sy + m[2] * sz;
        out[count * 3 + 1] = center.y + m[3] * sx + m[4] * sy + m[5] * sz;
        out[count * 3 + 2] = center.z + m[6] * sx + m[7] * sy + m[8] * sz;
        count++;
      }
    }
  }
  return count;
};

/**
 * Lever arm from the car's origin to `p`, and the velocity of the material
 * point there, into caller-owned vectors. Shared with ./car-car, which needs two
 * of each per contact and so cannot use this file's single scratch pair.
 */
export const pointKinematicsInto = (
  car: CarState,
  px: number,
  py: number,
  pz: number,
  rOut: Vec3,
  velOut: Vec3
): void => {
  rOut.x = px - car.position.x;
  rOut.y = py - car.position.y;
  rOut.z = pz - car.position.z;
  const w = car.angularVelocity;
  velOut.x = car.velocity.x + w.y * rOut.z - w.z * rOut.y;
  velOut.y = car.velocity.y + w.z * rOut.x - w.x * rOut.z;
  velOut.z = car.velocity.z + w.x * rOut.y - w.y * rOut.x;
};

/** `pointKinematicsInto` into this file's scratch pair. */
const pointKinematics = (car: CarState, px: number, py: number, pz: number): void => {
  pointKinematicsInto(car, px, py, pz, _r, _pointVel);
};

/** Any unit vector perpendicular to the unit `n`, written into `out` at `o`.
 *  Shared with ./car-car — see `angularTerm`. */
export const tangentBasis = (n: Vec3, out: Float64Array, o: number): void => {
  // Cross with whichever axis n is least aligned to, so the result is never
  // near-degenerate.
  let ax = 0;
  let ay = 0;
  let az = 0;
  if (Math.abs(n.x) <= Math.abs(n.y) && Math.abs(n.x) <= Math.abs(n.z)) ax = 1;
  else if (Math.abs(n.y) <= Math.abs(n.z)) ay = 1;
  else az = 1;

  let t1x = n.y * az - n.z * ay;
  let t1y = n.z * ax - n.x * az;
  let t1z = n.x * ay - n.y * ax;
  const len = Math.hypot(t1x, t1y, t1z);
  t1x /= len;
  t1y /= len;
  t1z /= len;

  out[o] = t1x;
  out[o + 1] = t1y;
  out[o + 2] = t1z;
  out[o + 3] = n.y * t1z - n.z * t1y;
  out[o + 4] = n.z * t1x - n.x * t1z;
  out[o + 5] = n.x * t1y - n.y * t1x;
};

/**
 * One Gauss-Seidel sweep over every point of every contact patch.
 *
 * IMPULSES ARE ACCUMULATED AND THE ACCUMULATOR IS WHAT IS CLAMPED, not the
 * per-sweep delta. This is the whole reason the solver behaves: with four
 * coplanar points and a per-sweep `max(0, ...)`, the first point solved gets to
 * stop the entire car at one corner — a 1600 uu/s nose-on wall hit came out at
 * the 5.5 rad/s angular cap with 350 uu/s of sideways velocity — and the other
 * three cannot undo it, because a contact may only push. Clamping the TOTAL to
 * be non-negative lets a later sweep hand impulse back, and the four points then
 * converge on the load distribution of a real contact patch.
 *
 * Friction is accumulated in a fixed (t1, t2) basis per contact and clamped
 * inside the Coulomb cone `|jt| <= mu * jn` on the accumulated normal impulse —
 * clamping against a per-sweep normal impulse would make the friction limit
 * oscillate with the sweep.
 */
const solveSweep = (
  car: CarState,
  inertia: InertiaPerMass,
  scratch: CarArenaScratch,
  count: number
): void => {
  for (let i = 0; i < count; i++) {
    const n = scratch.contacts[i].normal;
    const points = scratch.patch[i];
    const t1x = scratch.tangents[i * 6];
    const t1y = scratch.tangents[i * 6 + 1];
    const t1z = scratch.tangents[i * 6 + 2];
    const t2x = scratch.tangents[i * 6 + 3];
    const t2y = scratch.tangents[i * 6 + 4];
    const t2z = scratch.tangents[i * 6 + 5];

    for (let p = 0; p < scratch.patchSize[i]; p++) {
      const slot = i * PATCH_MAX + p;
      const px = points[p * 3];
      const py = points[p * 3 + 1];
      const pz = points[p * 3 + 2];

      // --- normal
      pointKinematics(car, px, py, pz);
      const vn = _pointVel.x * n.x + _pointVel.y * n.y + _pointVel.z * n.z;
      const wanted = (scratch.bias[slot] - vn) / scratch.kn[slot];
      const totalN = Math.max(0, scratch.accN[slot] + wanted);
      const dn = totalN - scratch.accN[slot];
      scratch.accN[slot] = totalN;
      if (dn !== 0) applyImpulse(car, inertia, _r, n.x * dn, n.y * dn, n.z * dn);

      if (totalN === 0) continue;

      // --- friction
      pointKinematics(car, px, py, pz);
      const v1 = _pointVel.x * t1x + _pointVel.y * t1y + _pointVel.z * t1z;
      const v2 = _pointVel.x * t2x + _pointVel.y * t2y + _pointVel.z * t2z;

      let new1 = scratch.accT1[slot] - v1 / scratch.k1[slot];
      let new2 = scratch.accT2[slot] - v2 / scratch.k2[slot];
      const limit = ARENA_COLLISION_FRICTION * totalN;
      const mag = Math.hypot(new1, new2);
      if (mag > limit) {
        const s = limit / mag;
        new1 *= s;
        new2 *= s;
      }
      const d1 = new1 - scratch.accT1[slot];
      const d2 = new2 - scratch.accT2[slot];
      scratch.accT1[slot] = new1;
      scratch.accT2[slot] = new2;
      if (d1 !== 0 || d2 !== 0) {
        applyImpulse(
          car, inertia, _r,
          t1x * d1 + t2x * d2,
          t1y * d1 + t2y * d2,
          t1z * d1 + t2z * d2
        );
      }
    }
  }
};

/**
 * One tick of car-body-vs-arena contact. Mutates position, velocity and angular
 * velocity; returns the number of contacts resolved on the FIRST pass, which is
 * what a caller wants for "did the body touch anything this tick" (a renderer
 * scrape effect, or the test that asserts driving never touches).
 *
 * A no-op on an arena with no `collideOBB` — see the note on that method.
 */
export const stepCarArenaContacts = (
  car: CarState,
  arena: ArenaGeometry,
  scratch: CarArenaScratch
): number => {
  if (arena.collideOBB === undefined) return 0;

  const inertia = carInertiaPerMass(car.config);
  let firstPassCount = 0;
  for (let pass = 0; pass < CONTACT_PASSES; pass++) {
    const box = carHitboxInto(scratch.box, car.position, car.orientation, car.config);
    const count = arena.collideOBB(box, scratch.contacts);
    if (pass === 0) firstPassCount = count;
    if (count === 0) break;

    // Depenetrate first, all of them, so the patches are built at the resolved
    // pose rather than at whichever intermediate one the loop happened to hit.
    for (let i = 0; i < count; i++) {
      const contact = scratch.contacts[i];
      if (contact.depth <= DEPTH_EPSILON) continue;
      car.position.x += contact.normal.x * contact.depth;
      car.position.y += contact.normal.y * contact.depth;
      car.position.z += contact.normal.z * contact.depth;
    }

    // Build the patches at the depenetrated pose, and with them the restitution
    // target for each point: the approach speed measured ONCE, before any
    // impulse. Re-reading it inside the sweeps would treat the same collision as
    // a fresh one eight times over and pump energy into the car.
    carHitboxInto(box, car.position, car.orientation, car.config);
    for (let i = 0; i < count; i++) {
      const contact = scratch.contacts[i];
      const points = scratch.patch[i];
      const size = supportPoints(
        box.center, box.orientation, box.halfExtents, contact.normal, points
      );
      scratch.patchSize[i] = size;
      tangentBasis(contact.normal, scratch.tangents, i * 6);
      const t = scratch.tangents;
      const to = i * 6;
      const n = contact.normal;

      for (let p = 0; p < size; p++) {
        const slot = i * PATCH_MAX + p;
        pointKinematics(car, points[p * 3], points[p * 3 + 1], points[p * 3 + 2]);
        const vn = _pointVel.x * n.x + _pointVel.y * n.y + _pointVel.z * n.z;
        scratch.bias[slot] = vn < 0 ? -ARENA_COLLISION_RESTITUTION * vn : 0;
        scratch.kn[slot] = 1 + angularTerm(car.orientation, inertia, _r, n.x, n.y, n.z);
        scratch.k1[slot] =
          1 + angularTerm(car.orientation, inertia, _r, t[to], t[to + 1], t[to + 2]);
        scratch.k2[slot] =
          1 + angularTerm(car.orientation, inertia, _r, t[to + 3], t[to + 4], t[to + 5]);
        scratch.accN[slot] = 0;
        scratch.accT1[slot] = 0;
        scratch.accT2[slot] = 0;
      }
    }

    for (let iter = 0; iter < SOLVER_ITERATIONS; iter++) solveSweep(car, inertia, scratch, count);

    clampLengthBy(car.velocity, CAR_MAX_SPEED);
    clampLengthBy(car.angularVelocity, CAR_MAX_ANGULAR_SPEED);
  }
  return firstPassCount;
};
